// FLOW Platform — FHT game shell.
//
// What this module does in Step 1:
//
//   1. Ensure we have a guest token (mint via POST /guest/init if needed,
//      cache in localStorage under TOKEN_KEY).
//   2. Fetch today's puzzle via GET /games/fht/today.
//   3. Render: start word, end word, tier label. No game loop yet —
//      that's Step 2.
//
// Failure modes handled:
//
//   - First-visit, no token: mint, save, continue.
//   - Stored token rejected by the server (`unauthorized`): clear,
//     re-mint, retry exactly once. (Common when a guest session expires
//     server-side or the row was wiped during an admin reset.)
//   - No puzzle published for today (`not_found`): explicit message.
//   - Network / server failure: friendly message + retry button.
//
// All status changes are announced through the polite live region so
// screen readers keep up.

import { createApiClient, FlowApiError } from './api-client.js';
import { createLiveRegion, focusElement } from './a11y.js';
import { mountGame, clearSavedState, activeSeconds } from './game.js';
import { mountPostGame, bottomShare, showBottomShare } from './post-game.js';
import { openSheet as openSheetCore, buildHelpContent } from './sheets.js';
import { setHowContext, openHow, autoHow, howCtl } from './howto.js';

const TOKEN_KEY = 'flow_guest_token';

// Public for tests / debugging only — not used by the shell at runtime.
export const __INTERNALS = { TOKEN_KEY };

/**
 * Read/write the cached guest token in localStorage. Wrapped so we can
 * survive storage being unavailable (private browsing edge cases).
 */
const tokenStore = {
  get() {
    try {
      return window.localStorage.getItem(TOKEN_KEY);
    } catch (e) {
      return null;
    }
  },
  set(token) {
    try {
      window.localStorage.setItem(TOKEN_KEY, token);
    } catch (e) {
      // Storage unavailable — the token only lives for this page load.
      // The HttpOnly cookie the server set will still keep the session
      // working as long as the browser permits cookies.
    }
  },
  clear() {
    try {
      window.localStorage.removeItem(TOKEN_KEY);
    } catch (e) {
      // ignore
    }
  },
};

/**
 * Best-effort IANA timezone detection. Sent on /guest/init so the
 * server can pin it for "today" math on the first play. We don't fail
 * if the browser can't tell us — the server falls back gracefully.
 */
function detectTimezone() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz || null;
  } catch (e) {
    return null;
  }
}

/**
 * Boot the shell onto the given mount node.
 *
 *   mount  - The element the shortcode rendered (`<div id="flow-fht-root">`).
 *   config - { restBase: string }  the flow/v1 base URL.
 */
// ---------------------------------------------------------------------------
// Beta tracking — remembers which puzzles have been played so the
// "Next puzzle" button always advances to a fresh one.
// ---------------------------------------------------------------------------

const BETA_PLAYED_KEY = 'flow_fht_beta_played';
const BETA_TIER_KEY   = 'flow_fht_beta_tier';

function betaGetPlayed() {
  try {
    const raw = window.localStorage.getItem(BETA_PLAYED_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch (e) { return new Set(); }
}

function betaMarkPlayed(puzzleId) {
  try {
    const played = betaGetPlayed();
    played.add(Number(puzzleId));
    window.localStorage.setItem(BETA_PLAYED_KEY, JSON.stringify([...played]));
  } catch (e) { /* ignore */ }
}

export function betaGetLastTier() {
  try { return window.localStorage.getItem(BETA_TIER_KEY) || 'easy'; } catch(e) { return 'easy'; }
}

export function betaSetLastTier(tier) {
  try { window.localStorage.setItem(BETA_TIER_KEY, tier); } catch(e) { /* ignore */ }
}

// ---------------------------------------------------------------------------
// Daily model — level selection and the completed-today record.
//
// The live game is one shared puzzle per level per day. The level rides on
// the URL (?lvl=2) so a shared Level 2 link lands the friend on the same
// puzzle; otherwise we remember the player's last choice.
// ---------------------------------------------------------------------------

const LEVEL_KEY = 'flow_fht_level';

/* ONE PUZZLE A DAY, LIKE THE OTHER FOUR (2.14.13). Jeff, 2026-09-28: "for
   thruword, remove level one and make it like all the others." The Level 1 /
   Level 2 choice is gone; the daily puzzle is what was Level 2 (the medium
   tier). An old ?lvl=1 link, or a device that remembered Level 1, lands on it
   too. This reverses Jeff's own August call ("keep the two player-facing
   levels for now", thruWord_FRESH_START.md §8). */
export function currentLevel() {
  return 2;
}

// Once today's puzzle is finished, the day locks: a compact record of the
// final board is kept so a returning visit lands on the result screen (with
// the countdown to tomorrow) instead of a replayable board. Keyed by date +
// level; yesterday's records are cleaned up as new days are saved.
function doneKey(date, level) {
  return 'flow_fht_done_' + date + '_L' + level;
}

function saveDoneRecord(date, level, finalState, playResponse) {
  try {
    const s = { ...finalState };
    delete s.current_input;
    delete s._hint_cache;
    delete s._test_mode;
    window.localStorage.setItem(
      doneKey(date, level),
      JSON.stringify({ state: s, play: playResponse || {} }),
    );
    // Sweep older done-records so localStorage doesn't accumulate forever.
    const prefix = 'flow_fht_done_';
    const keep = new Set([doneKey(date, 1), doneKey(date, 2)]);
    for (let i = window.localStorage.length - 1; i >= 0; i--) {
      const k = window.localStorage.key(i);
      if (k && k.indexOf(prefix) === 0 && !keep.has(k)) {
        window.localStorage.removeItem(k);
      }
    }
  } catch (e) { /* ignore */ }
}

function loadDoneRecord(date, level) {
  try {
    const raw = window.localStorage.getItem(doneKey(date, level));
    if (!raw) return null;
    const rec = JSON.parse(raw);
    return rec && rec.state ? rec : null;
  } catch (e) {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Beta browse access — the tester's key.
//
// The daily model is the player experience: one puzzle per level per day,
// then the door closes. Testers need the whole library. The browse door
// opens for logged-in admins automatically, and for anyone who arrives on
// a link carrying ?beta=1 — which is remembered on that device, so testers
// keep their key after the parameter falls off the URL.
// ---------------------------------------------------------------------------

const BETA_ACCESS_KEY = 'flow_fht_beta_access';

function betaBrowseAllowed() {
  const cfg = window.flowFhtConfig || {};
  if (cfg.isAdmin) return true;
  try {
    if (new URLSearchParams(window.location.search).has('beta')) {
      try { window.localStorage.setItem(BETA_ACCESS_KEY, '1'); } catch (e) { /* ignore */ }
      return true;
    }
  } catch (e) { /* ignore */ }
  try {
    return window.localStorage.getItem(BETA_ACCESS_KEY) === '1';
  } catch (e) {
    return false;
  }
}

// Returns the next unplayed puzzle id for a given tier (or any tier if not specified)
export function betaNextUnplayedId(puzzleIndex, tier) {
  const played = betaGetPlayed();
  const filtered = tier
    ? puzzleIndex.filter(p => String(p.tier).toLowerCase() === String(tier).toLowerCase())
    : puzzleIndex;
  const sorted = [...filtered].sort((a, b) => Number(a.id) - Number(b.id));
  for (const p of sorted) {
    if (!played.has(Number(p.id))) return Number(p.id);
  }
  // All played in this tier — wrap around
  return sorted.length > 0 ? Number(sorted[0].id) : null;
}

/* 2.14.31 (Jeff, 2026-10-08: "Have a button while in beta to show the next game that I
   haven't played, not the days game. / Reset the boards and scramble them"; 2026-10-09:
   "G - okay"): a puzzle picked at random from those with no play on this device, never
   the one on screen, or null when there is none. Played means finished in free play,
   finished today, or begun (a guess made, the hint used, a word locked), as Next has
   read it since 2.14.29. Nothing is stored and no day on the calendar moves. */
export function betaRandomUnplayedId(list, currentId) {
  const done = betaGetPlayed();
  const open = (list || []).map((p) => Number(p.id))
    .filter((id) => id !== Number(currentId) && !done.has(id) && !puzzleBegun(id));
  if (!open.length) return null;
  let k = Math.floor(Math.random() * open.length);
  if (!(k >= 0)) k = 0;
  if (k >= open.length) k = open.length - 1;
  return open[k];
}

// Returns all puzzles for a tier sorted by id
function betaTierPuzzles(puzzleIndex, tier) {
  return [...puzzleIndex]
    .filter(p => String(p.tier).toLowerCase() === String(tier).toLowerCase())
    .sort((a, b) => Number(a.id) - Number(b.id));
}

// Next puzzle id in tier sequence after currentId (wraps around)
function betaNextInTier(puzzleIndex, currentId, tier) {
  const list = betaTierPuzzles(puzzleIndex, tier);
  const idx = list.findIndex(p => Number(p.id) === Number(currentId));
  if (idx === -1 || idx >= list.length - 1) return list[0] ? Number(list[0].id) : null;
  return Number(list[idx + 1].id);
}

/* Row one's label and row two's level tabs live in the shell, which is built
   once; the puzzle screens fill them. */
let HEADER_LABEL = null;
// What the bottom Share button shares: set by whichever screen is showing (2.14.17).
let SHARE_STATE = null;
if (typeof document !== 'undefined') {
  document.addEventListener('click', (e) => {
    const b = e.target && e.target.closest ? e.target.closest('#flow-fht-share') : null;
    if (!b) return;
    e.preventDefault();
    bottomShare(SHARE_STATE ? SHARE_STATE() : {}, b);
  });
}
let LEVEL_SLOT = null;

export function bootShell(mount, config) {
  // Defense against accidental double-boot — if two copies of the
  // bundle end up on the page, the second call must not wipe and
  // rebuild the live mount (that's what produced ghost sheets / two
  // game instances earlier).
  if (mount.dataset && mount.dataset.flowFhtBooted === '1') {
    return;
  }
  // Reset whatever was in the mount and put our scaffold in place.
  mount.innerHTML = '';
  mount.classList.add('flow-fht-shell');
  if (mount.dataset) {
    mount.dataset.flowFhtBooted = '1';
  }

  // The visible region (heading, puzzle, controls) goes inside a <main>
  // landmark so assistive tech can jump straight to it.
  const main = document.createElement('main');
  main.className = 'flow-fht-main';
  main.setAttribute('aria-labelledby', 'flow-fht-heading');
  mount.appendChild(main);

  // THE PLATFORM'S TWO ROWS (2.13.0 standard; thruWord joins in 2.14.12 —
  // Jeff, 2026-09-28: "compare each game board and ensure that they are
  // similar in layout and presentation").
  //   Row one, WHAT you are playing: the name left, "#77 · Mon, Sep 28" right.
  //   Row two, what you can DO: the icons left (? first, then this game's own,
  //   then the platform's feedback pencil), the ‹ Next › walkers right
  //   where the other games keep their walkers.
  const header = document.createElement('div');
  header.className = 'flow-fht-header-row';
  main.appendChild(header);

  const heading = document.createElement('h2');
  heading.id = 'flow-fht-heading';
  heading.className = 'flow-fht-heading';
  heading.innerHTML = '<span class="flow-fht-heading-thru">thru</span>Word';
  header.appendChild(heading);

  const dailyLabel = document.createElement('span');
  dailyLabel.className = 'flow-fht-daily-label flow-fht-tier-counter';
  header.appendChild(dailyLabel);
  HEADER_LABEL = dailyLabel;

  const ctlrow = document.createElement('div');
  ctlrow.className = 'flow-fht-ctlrow';
  main.appendChild(ctlrow);

  const headerActions = document.createElement('div');
  headerActions.className = 'flow-fht-header-actions';
  headerActions.setAttribute('data-flow-feedback-slot', 'icon');
  ctlrow.appendChild(headerActions);

  const levelSlot = document.createElement('div');
  levelSlot.className = 'flow-fht-level-slot';
  ctlrow.appendChild(levelSlot);
  LEVEL_SLOT = levelSlot;

  // "Uses: Word retrieval · Working memory" — the platform's line under every
  // game's header since 2.14.2. It describes what the puzzle asks of you; it
  // never says the game improves anything (the no-claim rule, 2026-09-16,
  // agreed by Jeff 2026-09-21). Each term taps open its one-line gloss.
  main.appendChild(buildUsesLine([
    ['Word retrieval', 'Getting the word you know out when you need it'],
    ['Working memory', 'Holding both ends of the bridge in mind at once'],
  ]));

  function openSheet(id) {
    const trigger = (id === 'help') ? helpBtn : (id === 'stats' ? statsBtn : settingsBtn);
    const opts = (id === 'stats') ? { api, testMode: !!(window.flowFhtConfig && window.flowFhtConfig.testMode) } : undefined;
    openSheetCore(mount, id, trigger, config && config.version, opts);
  }

  // Bar-chart glyph (monochrome, inherits ink colour) to match the ? and gear.
  const STATS_ICON_SVG = '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><line x1="6" y1="20" x2="6" y2="13"></line><line x1="12" y1="20" x2="12" y2="9"></line><line x1="18" y1="20" x2="18" y2="5"></line></svg>';

  const statsBtn = makeIconButton({
    label: 'Your stats',
    svg: STATS_ICON_SVG,
    onClick: () => openSheet('stats'),
  });
  /* 2.14.37: ? opens How to play, the practice round; the written rules are folded under it. If the platform's frame
     never arrives, the written rules open in thruWord's own sheet as before. */
  const helpBtn = makeIconButton({
    label: 'How to play',
    glyph: '?',
    onClick: () => { if (!openHow()) openSheet('help'); },
  });
  const settingsBtn = makeIconButton({
    label: 'Settings',
    glyph: '\u2699', // gear
    onClick: () => openSheet('settings'),
  });
  setHowContext({ button: helpBtn, rules: buildHelpContent });
  headerActions.appendChild(helpBtn);
  headerActions.appendChild(statsBtn);
  headerActions.appendChild(settingsBtn);

  // Body region — where loading / error / puzzle content swap in and out.
  const body = document.createElement('div');
  body.className = 'flow-fht-body';
  // The body gets aria-busy=true while we're loading, and false otherwise.
  body.setAttribute('aria-busy', 'true');
  main.appendChild(body);

  // Polite live region for screen readers. Lives outside the visual
  // body so we don't disturb its layout.
  const live = createLiveRegion(mount);

  const api = createApiClient({
    restBase: config.restBase,
    getToken: tokenStore.get,
  });

  // Kick off the flow.
  loadTodaysPuzzle({ api, body, live, retryOnAuthFail: true });
}

/**
 * Detect IANA timezone (used at puzzle submit). Cached call is fine.
 */
function detectTimezoneCached() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch (e) {
    return 'UTC';
  }
}

/**
 * The local-date the player is playing on, in their timezone. Sent to
 * /plays so the server can pin streak math to the player's "today" even
 * if they cross midnight UTC while playing.
 */
function localDateToday() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return y + '-' + m + '-' + day;
}

/**
 * Top-level "load and render" flow. Pulled out of bootShell so the
 * Retry button can call it again.
 *
 * In test mode (admin launched the page with ?fht_test=N), the
 * shortcode pre-bakes the puzzle into the config. We use it directly
 * and skip the /today fetch entirely. We still mint a token so the
 * stats panel in the post-game screen can read /streak and /stats.
 */
async function loadTodaysPuzzle({ api, body, live, retryOnAuthFail }) {
  renderLoading(body, live);

  const cfg = window.flowFhtConfig || {};
  // ONE PUZZLE A DAY (the player's switch, flow-beta-v1.js v2): a library URL
  // (?fht_test=N) is not honoured while it is on — the page goes back to today.
  if (oneADayOn() && new URLSearchParams(window.location.search).has('fht_test')) {
    const u = new URL(window.location.href);
    u.searchParams.delete('fht_test');
    window.location.replace(u.toString());
    return;
  }
  const testMode = !!cfg.testMode && cfg.testPuzzle && cfg.testPuzzle.puzzle_id;

  // 1. Ensure we have a token. Needed for /plays writes (regular play) and
  // /streak + /stats reads (both modes).
  let token = tokenStore.get();
  if (!token) {
    try {
      token = await mintToken(api);
      tokenStore.set(token);
    } catch (err) {
      renderError(body, live, err, () => loadTodaysPuzzle({ api, body, live, retryOnAuthFail }));
      return;
    }
  }

  // 2. Fetch the puzzle — except in test mode where the shortcode has
  // already inlined it for us.
  let puzzle;
  const level = currentLevel();
  if (testMode) {
    puzzle = cfg.testPuzzle;
  } else {
    // Daily model: everyone on a level plays the same puzzle today. The
    // client sends its local date so the flip happens at the PLAYER's
    // midnight, and the level so Level 1 / Level 2 each get their own daily.
    try {
      const res = await api.get(
        'games/fht/today?level=' + level +
        '&local_date=' + encodeURIComponent(localDateToday()),
      );
      puzzle = res.data;
    } catch (err) {
      if (
        err instanceof FlowApiError &&
        err.code === 'unauthorized' &&
        retryOnAuthFail
      ) {
        tokenStore.clear();
        loadTodaysPuzzle({ api, body, live, retryOnAuthFail: false });
        return;
      }
      // Already finished today's puzzle on this level on this device? Show
      // the finished board instead of an error — the record on the device
      // has everything the result screen needs, so a server hiccup (or a
      // missing schedule entry) shouldn't hide a completed day.
      const doneAnyway = loadDoneRecord(localDateToday(), level);
      if (doneAnyway && doneAnyway.state) {
        const s = doneAnyway.state;
        const stubPuzzle = {
          puzzle_id: Number(s.puzzle_id) || 0,
          level: level,
          tier: s.tier || (level === 2 ? 'medium' : 'easy'),
          daily_number: Number(s.daily_number) || 0,
        };
        renderLockedDay(body, live, stubPuzzle, doneAnyway, api, level);
        return;
      }
      renderError(body, live, err, () => loadTodaysPuzzle({ api, body, live, retryOnAuthFail: true }));
      return;
    }

    // Already finished today's puzzle on this level? Land on the locked
    // result screen — today's play is done, tomorrow's is coming.
    const done = loadDoneRecord(localDateToday(), level);
    if (done && Number(done.state.puzzle_id) === Number(puzzle.puzzle_id)) {
      renderLockedDay(body, live, puzzle, done, api, level);
      return;
    }
  }

  // 3. Render the puzzle. testMode flows through to the game and
  // post-game so /plays isn't called and the banner says "Test mode".
  renderPuzzle(body, live, puzzle, api, testMode);
}

/**
 * Hit POST /guest/init and return the token string. Throws on any
 * failure — the caller decides how to surface it.
 */
async function mintToken(api) {
  const timezone = detectTimezone();
  const body = {};
  if (timezone) {
    body.timezone = timezone;
  }
  const res = await api.post('guest/init', body);
  const data = res.data || {};
  if (typeof data.guest_token !== 'string' || data.guest_token === '') {
    throw new FlowApiError(
      'internal_error',
      'The server did not return a guest token.',
      res.meta && res.meta.request_id,
      [],
    );
  }
  return data.guest_token;
}

// ---------------------------------------------------------------------------
// Rendering — plain DOM, no framework. Each renderX() fully replaces the
// contents of `body` so we don't need to track previous nodes.
// ---------------------------------------------------------------------------

function renderLoading(body, live) {
  body.setAttribute('aria-busy', 'true');
  body.innerHTML = '';

  const wrap = document.createElement('div');
  wrap.className = 'flow-fht-loading';

  const dot = document.createElement('span');
  dot.className = 'flow-fht-loading-dot';
  dot.setAttribute('aria-hidden', 'true');
  wrap.appendChild(dot);

  const text = document.createElement('p');
  text.className = 'flow-fht-loading-text';
  text.textContent = 'Loading today\u2019s puzzle\u2026';
  wrap.appendChild(text);

  body.appendChild(wrap);
  live.announce('Loading today\u2019s puzzle.');
}

function renderError(body, live, err, retry) {
  body.setAttribute('aria-busy', 'false');
  body.innerHTML = '';

  const missingDaily = (err instanceof FlowApiError && err.code === 'not_found');

  // A missing daily puzzle never replaces the game with a bare error card.
  // The page keeps its normal frame — the two rows stay at
  // the top, so the player can always hop to the other level's puzzle.
  let host = body;
  if (missingDaily) {
    const lvl = currentLevel();
    const stub = {
      level: lvl,
      tier: lvl === 2 ? 'medium' : 'easy',
      daily_number: 0,
    };
    const frame = document.createElement('div');
    frame.className = 'flow-fht-puzzle';
    frame.appendChild(buildDailyHeaderRow(stub));
    body.appendChild(frame);
    host = frame;
  }

  const wrap = document.createElement('div');
  wrap.className = 'flow-fht-error';
  wrap.setAttribute('role', 'alert');

  const heading = document.createElement('h2');
  heading.className = 'flow-fht-error-heading';
  heading.textContent = errorHeadingFor(err);
  wrap.appendChild(heading);

  const message = document.createElement('p');
  message.className = 'flow-fht-error-message';
  message.textContent = errorBodyFor(err);
  wrap.appendChild(message);

  if (typeof retry === 'function') {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'flow-fht-button';
    button.textContent = 'Try again';
    button.addEventListener('click', () => {
      retry();
    });
    wrap.appendChild(button);
  }

  host.appendChild(wrap);
  live.announce(errorHeadingFor(err) + '. ' + errorBodyFor(err));
  // Pull focus to the heading so the screen reader announces the error
  // and keyboard users can find the retry button by tabbing once.
  focusElement(heading);
}

function renderPuzzle(body, live, puzzle, api, testMode) {
  body.setAttribute('aria-busy', 'false');
  body.innerHTML = '';

  const content = (puzzle && puzzle.content) || {};
  const startWord = content.start_word || '';
  const endWord = content.end_word || '';
  const tier = puzzle.tier || content.tier || 'unknown';
  const par = (typeof content.par === 'number') ? content.par : null;
  const cfg = window.flowFhtConfig || {};
  const puzzleIndex = cfg.puzzleIndex || [];

  const wrap = document.createElement('div');
  wrap.className = 'flow-fht-puzzle';

  // Row one's label and row two's walkers. Daily mode: "#77 · date" and the
  // walkers. Library mode (?fht_test=N): the walkers, and a way back to today.
  if (!testMode) {
    wrap.appendChild(buildDailyHeaderRow(puzzle));
  } else if (puzzleIndex.length > 0) {
    const tierRow = document.createElement('div');
    tierRow.className = 'flow-fht-tier-row';
    tierRow.setAttribute('data-flow-beta', '');   // free play is beta's, like the other games' walkers

    // The way back out of the library, to today's daily puzzle.
    const backBtn = document.createElement('button');
    backBtn.type = 'button';
    backBtn.className = 'flow-fht-beta-link';
    backBtn.textContent = '\u2190 Today';
    backBtn.setAttribute('aria-label', 'Back to today\u2019s daily puzzle');
    backBtn.addEventListener('click', () => {
      const u = new URL(window.location.href);
      u.searchParams.delete('fht_test');
      window.location.href = u.toString();
    });
    tierRow.appendChild(backBtn);

    /* Library play (?fht_test=N): the same walkers as the daily page, around
       this puzzle; row one names the puzzle and carries no date, because it is
       not today's. */
    mountWalkers(Number(puzzle.puzzle_id) || 0);
    if (HEADER_LABEL) {
      const tn = Number(puzzle.daily_number) || 0;
      HEADER_LABEL.textContent = tn > 0 ? ('#' + tn) : 'Library';
    }

    wrap.appendChild(tierRow);
  }

  // Game container
  const gameContainer = document.createElement('div');
  gameContainer.className = 'flow-fht-game-host';
  wrap.appendChild(gameContainer);

  body.appendChild(wrap);

  const normalisedPuzzle = {
    puzzle_id: puzzle.puzzle_id,
    puzzle_version_id: puzzle.puzzle_version_id,
    start_word: startWord,
    end_word: endWord,
    tier,
    par,
    level: Number(puzzle.level) || (tier === 'medium' ? 2 : 1),
    daily_number: Number(puzzle.daily_number) || 0,
    publish_date: puzzle.publish_date || '',
  };

  let game = null;
  showBottomShare(true);   // 2.14.29: the end screen takes it off; the game screen has it
  SHARE_STATE = () => ({ ...normalisedPuzzle, ...((game && game.state) || {}), daily_number: normalisedPuzzle.daily_number });
  // 2.14.23: the designed lengths and fingerprints go to the game only, not into
  // the share state.
  game = mountGame(gameContainer, { ...normalisedPuzzle, design: content.design || null }, {
    testMode,
    onComplete: (finalState) => {
      handlePuzzleComplete({
        api, body, wrap, gameContainer,
        finalState, normalisedPuzzle, live, testMode,
      });
    },
  });

  // The platform's Feedback button asks the game where the player was
  // (flow-feedback-v1.js reads window.flowFeedbackContext). Until now thruWord
  // answered nothing, so a report arrived with no puzzle on it.
  window.flowFeedbackContext = function () {
    const st = (game && game.state) || {};
    return {
      puzzle_id: normalisedPuzzle.puzzle_id,
      daily_number: normalisedPuzzle.daily_number,
      level: normalisedPuzzle.level,
      tier: normalisedPuzzle.tier,
      status: st.status || '',
      guesses: Number(st.guess_count) || 0,
      hints_used: Math.max(0, (st.max_stars || 0) - (st.stars_remaining || 0)),
      slots_filled: (st.slots_filled || []).filter(Boolean).length,
      test_mode: !!testMode,
    };
  };

  /* 2.14.37: How to play opens itself on a puzzle nobody has played yet (no guess, no hint, no word in place) */
  setHowContext({
    puzzle: () => normalisedPuzzle.puzzle_id,
    fresh: () => {
      const st = (game && game.state) || {};
      return st.status === 'in-progress' && !(Number(st.guess_count) > 0) &&
        Math.max(0, (st.max_stars || 0) - (st.stars_remaining || 0)) === 0 &&
        !(st.slots_filled || []).some(Boolean) && !st.current_input;
    },
  });
  if (window.flowSharedReady && window.flowSharedReady.then) window.flowSharedReady.then(autoHow); else autoHow();
  window.__fhtHow = () => howCtl();

  live.announce(
    (testMode ? 'Test mode. ' : '') +
    'Puzzle ready. ' + formatTier(tier, par) + '. ' +
    'From ' + startWord.toUpperCase() + ' to ' + endWord.toUpperCase() + '.',
  );
}

/**
 * The daily header row for live mode (2.14.13: no levels): the walkers plus the
 * daily identity ("thruWord #N"). Switching levels navigates to today's
 * puzzle for that level (the ?lvl param rides on the URL so shares match).
 */
function buildDailyHeaderRow(puzzle) {
  const row = document.createElement('div');
  row.className = 'flow-fht-tier-row';

  const n = Number(puzzle.daily_number) || 0;
  // The number and the date: row one's right-hand side, the same place and
  // shape as "Board 18 of 154 · Mon, Sep 28" in the other games.
  const text = n > 0 ? ('#' + n + ' \u00b7 ' + todayLabel()) : todayLabel();
  if (HEADER_LABEL) {
    HEADER_LABEL.textContent = text;
  } else if (n > 0) {
    const label = document.createElement('span');
    label.className = 'flow-fht-tier-counter';
    label.textContent = text;
    row.appendChild(label);
  }
  // Row two's right-hand side: the walkers, as in every other game.
  mountWalkers(Number(puzzle.puzzle_id) || 0);
  return row;
}

/* ‹ and Next ›, the other four games' walkers (2.14.13, replacing the Level 1 /
   Level 2 tabs and the "Beta · browse" door). They step through the puzzle
   library in schedule order, one day at a time, and carry data-flow-beta so the
   platform's beta switch — and the player's One-puzzle-a-day switch — hide them
   exactly as they hide the other games'. A walked-to puzzle is library play:
   it never touches the streak or the stats. */
function dailyList() {
  const cfg = window.flowFhtConfig || {};
  return (cfg.puzzleIndex || []).filter((p) => String(p.tier).toLowerCase() === 'medium');
}
function mountWalkers(currentId) {
  const slot = LEVEL_SLOT;
  if (!slot) return;
  slot.innerHTML = '';
  const list = dailyList();
  if (!list.length) return;
  const walk = document.createElement('div');
  walk.className = 'flow-fht-walk';
  walk.setAttribute('data-flow-beta', '');
  const at = list.findIndex((p) => Number(p.id) === Number(currentId));
  const step = (d) => {
    const k = at < 0 ? (d > 0 ? 0 : list.length - 1) : ((at + d) % list.length + list.length) % list.length;
    return Number(list[k].id);
  };
  /* 2.14.31: the walker reads "Next unplayed" and goes to a RANDOM puzzle with no play on
     this device (betaRandomUnplayedId above); with every other one played it is simply
     the next in schedule order. Since 2.14.29 it went to the next unplayed one in that
     order. The ‹ still steps back one. */
  const nextUnplayed = () => {
    const id = betaRandomUnplayedId(list, currentId);
    return id === null ? step(1) : id;
  };
  const prev = document.createElement('button');
  prev.type = 'button';
  prev.className = 'flow-fht-walk-btn';
  prev.textContent = '\u2039';
  prev.setAttribute('aria-label', 'Previous puzzle');
  prev.addEventListener('click', () => navigateToTest(step(-1)));
  const next = document.createElement('button');
  next.type = 'button';
  next.className = 'flow-fht-walk-btn';
  next.textContent = 'Next unplayed \u203a';
  next.setAttribute('aria-label', 'Next unplayed puzzle');
  next.addEventListener('click', () => navigateToTest(nextUnplayed()));
  walk.appendChild(prev);
  walk.appendChild(next);
  slot.appendChild(walk);
}

/**
 * The locked-day screen: the player already finished today's puzzle on this
 * level, so a fresh visit lands on their result — chain, streak, share, and
 * the countdown to tomorrow — instead of a replayable board. The level
 * toggle stays live so they can hop to the other level's daily.
 */
function renderLockedDay(body, live, puzzle, doneRecord, api, level) {
  body.setAttribute('aria-busy', 'false');
  body.innerHTML = '';

  const wrap = document.createElement('div');
  wrap.className = 'flow-fht-puzzle';
  wrap.appendChild(buildDailyHeaderRow(puzzle));

  const host = document.createElement('div');
  host.className = 'flow-fht-game-host';
  wrap.appendChild(host);
  body.appendChild(wrap);

  const state = doneRecord.state || {};
  // Older records may predate the daily fields — backfill from the puzzle.
  if (!state.level) state.level = level;
  if (!state.daily_number) state.daily_number = Number(puzzle.daily_number) || 0;

  SHARE_STATE = () => state;
  mountPostGame(host, state, doneRecord.play || {}, api, { locked: true });

  live.announce(
    'You\u2019ve already played today\u2019s thruWord. ' +
    'Here\u2019s your result \u2014 the next puzzle arrives at midnight.',
  );
}

/**
 * Called when the game module reports a final state. In normal mode
 * we POST to /plays and clear localStorage on success. In test mode
 * we skip the network write entirely and jump straight to the
 * post-game screen.
 */async function handlePuzzleComplete({
  api, body, wrap, gameContainer, finalState, normalisedPuzzle, live, testMode,
}) {
  // Daily identity rides on the final state so the post-game screen and the
  // share text can say "thruWord #N · Level L" without re-fetching anything.
  finalState.level = normalisedPuzzle.level || finalState.level || 1;
  finalState.daily_number = normalisedPuzzle.daily_number || finalState.daily_number || 0;
  finalState.publish_date = normalisedPuzzle.publish_date || finalState.publish_date || '';

  // Beta tracking — mark this puzzle as played so it's skipped in future
  if (testMode) {
    betaMarkPlayed(finalState.puzzle_id);
    betaSetLastTier(finalState.tier || 'easy');
  }

  if (testMode) {
    clearSavedState(finalState.puzzle_id, true);
    const cfg = window.flowFhtConfig || {};
    mountPostGame(gameContainer, finalState, { test_mode: true }, api, {
      testMode: true,
      puzzleIndex: cfg.puzzleIndex || [],
      currentTier: finalState.tier || 'easy',
    });
    return;
  }

  // Active play time — counts moments between moves (capped), never the
  // hours a tab sat open. Falls back to wall-clock only for plays that
  // finished before active-time tracking existed.
  const timeSeconds = activeSeconds(finalState);

  const solutionChain = [
    String(normalisedPuzzle.start_word).toLowerCase(),
    ...finalState.slots_filled.map((w) => String(w || '').toLowerCase()),
    String(normalisedPuzzle.end_word).toLowerCase(),
  ];

  const resultPayload = {
    outcome: finalState.status === 'solved' ? 'solved' : 'failed',
    moves: solutionChain.length - 1,
    time_seconds: timeSeconds,
    solution_chain: solutionChain,
    hints_used: Number(finalState.hints_used) || 0,
    reveals_used: Number(finalState.reveals_used) || 0,
    invalid_attempts: Number(finalState.invalid_attempts) || 0,
    guesses: Number(finalState.guess_count) || 0,
    backtracks: 0,
  };

  showSavingResult(wrap, gameContainer);

  try {
    const res = await api.post('games/fht/plays', {
      puzzle_id: finalState.puzzle_id,
      outcome: resultPayload.outcome,
      local_date: localDateToday(),
      timezone: detectTimezoneCached(),
      result_payload: resultPayload,
    });
    clearSavedState(finalState.puzzle_id);
    // 2.14.29: a daily finished for real is a played puzzle, as one finished in free play
    // is, so Next passes it on later days (its saved state and its day's record both go).
    betaMarkPlayed(finalState.puzzle_id);
    // Lock the day: a return visit lands on this result screen until the
    // next puzzle arrives at midnight.
    saveDoneRecord(localDateToday(), finalState.level || 1, finalState, res.data || {});
    mountPostGame(gameContainer, finalState, res.data || {}, api);
  } catch (err) {
    renderSubmitError(gameContainer, err, () => {
      handlePuzzleComplete({
        api, body, wrap, gameContainer, finalState, normalisedPuzzle, live, testMode,
      });
    });
  }
}

function showSavingResult(wrap, gameContainer) {
  gameContainer.innerHTML = '';
  const saving = document.createElement('div');
  saving.className = 'flow-fht-saving';
  saving.setAttribute('aria-live', 'polite');
  saving.textContent = 'Saving your result\u2026';
  gameContainer.appendChild(saving);
}

function renderSubmitError(gameContainer, err, retry) {
  gameContainer.innerHTML = '';
  const wrap = document.createElement('div');
  wrap.className = 'flow-fht-error';
  wrap.setAttribute('role', 'alert');

  const heading = document.createElement('h2');
  heading.className = 'flow-fht-error-heading';
  heading.textContent = 'Couldn\u2019t save your result';
  wrap.appendChild(heading);

  const msg = document.createElement('p');
  msg.className = 'flow-fht-error-message';
  msg.textContent = (err instanceof FlowApiError && err.message)
    ? err.message
    : 'A network problem stopped your result from saving. Try again.';
  wrap.appendChild(msg);

  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'flow-fht-button';
  button.textContent = 'Try again';
  button.addEventListener('click', () => retry());
  wrap.appendChild(button);

  gameContainer.appendChild(wrap);
  focusElement(heading);
}

// ---------------------------------------------------------------------------
// Small DOM builders.
// ---------------------------------------------------------------------------

function makeWordTile(word, role) {
  const tile = document.createElement('div');
  tile.className = 'flow-fht-tile flow-fht-tile-' + role;
  tile.textContent = word || '';
  return tile;
}

function makeArrow() {
  const arrow = document.createElement('span');
  arrow.className = 'flow-fht-arrow';
  arrow.setAttribute('aria-hidden', 'true');
  arrow.textContent = '\u2192';
  return arrow;
}

function makeBridgeStub() {
  const tile = document.createElement('div');
  tile.className = 'flow-fht-tile flow-fht-tile-bridge-stub';
  tile.textContent = '\u2026';
  tile.setAttribute('aria-label', 'Bridge word (gameplay coming soon)');
  return tile;
}

// ---------------------------------------------------------------------------
// Message helpers.
// ---------------------------------------------------------------------------

/* 2.14.29: has this puzzle been begun or finished on this device? Read from the game's
   own saved states (free play and the day's) and today's finished record. */
function puzzleBegun(id) {
  const begun = (raw) => {
    if (!raw) return false;
    try {
      const st = JSON.parse(raw);
      if (!st || typeof st !== 'object') return false;
      return st.status !== 'in-progress' || Number(st.guess_count) > 0 || Number(st.hints_used) > 0 ||
        (Array.isArray(st.slots_filled) && st.slots_filled.some(Boolean));
    } catch (e) { return false; }
  };
  try {
    if (begun(window.localStorage.getItem('flow_fht_test_state:' + id))) return true;
    if (begun(window.localStorage.getItem('flow_fht_state:' + id))) return true;
    for (const level of [1, 2]) {
      const rec = loadDoneRecord(localDateToday(), level);
      if (rec && rec.state && Number(rec.state.puzzle_id) === Number(id)) return true;
    }
  } catch (e) { /* storage blocked: nothing is known to be played */ }
  return false;
}

function navigateToTest(id) {
  const u = new URL(window.location.href);
  u.searchParams.set('fht_test', String(id));
  window.location.href = u.toString();
}

function formatTier(tier, par) {
  const t = String(tier || '').toLowerCase();
  const label = 'Today';   // one puzzle a day since 2.14.13; the tier is not the player's business
  const parText = (typeof par === 'number') ? ' \u00b7 par ' + par : '';
  return label + parText;
}

function errorHeadingFor(err) {
  if (!(err instanceof FlowApiError)) {
    return 'Something went wrong';
  }
  switch (err.code) {
    case 'not_found':
      return 'This puzzle isn\u2019t available';
    case 'unauthorized':
      return 'Session expired';
    case 'network_error':
      return 'Can\u2019t reach the server';
    case 'rate_limited':
      return 'Too many requests';
    default:
      return 'Something went wrong';
  }
}

function errorBodyFor(err) {
  if (!(err instanceof FlowApiError)) {
    return 'Please try again in a moment.';
  }
  switch (err.code) {
    case 'not_found':
      return 'We couldn\u2019t find today\u2019s puzzle. Try again in a moment.';
    case 'unauthorized':
      return 'Your session needs to be refreshed. Try again to start a new one.';
    case 'network_error':
      return 'Check your internet connection and try again.';
    case 'rate_limited':
      return 'Please wait a moment before trying again.';
    default:
      return err.message || 'Please try again in a moment.';
  }
}

/**
 * Build a small round icon button used in the header (Help, Settings).
 * The glyph is the visible character; the label is what screen readers
 * announce.
 */
function makeIconButton({ label, glyph, svg, onClick }) {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'flow-fht-icon-button';
  if (svg) {
    btn.innerHTML = svg;
  } else {
    btn.textContent = glyph;
  }
  btn.setAttribute('aria-label', label);
  btn.setAttribute('title', label);
  btn.addEventListener('click', onClick);
  return btn;
}

/** The player's one-puzzle-a-day switch, kept by the platform's beta widget. */
function oneADayOn() {
  try { return !!(window.flowBeta && window.flowBeta.oneADay && window.flowBeta.oneADay()); } catch (e) { return false; }
}
// When the switch goes on while browsing the library, go back to today.
window.addEventListener('flow-one-a-day', (e) => {
  if (e && e.detail && e.detail.on && new URLSearchParams(window.location.search).has('fht_test')) {
    const u = new URL(window.location.href);
    u.searchParams.delete('fht_test');
    window.location.href = u.toString();
  }
});

/** Today's date, the reader's own way round: "Fri, Sep 26". */
function todayLabel() {
  try {
    return new Date().toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  } catch (e) { return ''; }
}

/**
 * The "Uses:" line. terms = [[name, gloss], ...]. Tapping a term shows its
 * gloss under the line; tapping again, or another term, swaps or hides it.
 */
function buildUsesLine(terms) {
  const row = document.createElement('div');
  row.className = 'flow-fht-uses';
  const lab = document.createElement('span');
  lab.className = 'flow-fht-uses-label';
  lab.textContent = 'Uses';
  row.appendChild(lab);
  row.appendChild(document.createTextNode(' '));
  const exp = document.createElement('div');
  exp.className = 'flow-fht-uses-exp';
  exp.hidden = true;
  terms.forEach(([name, gloss], i) => {
    if (i > 0) row.appendChild(document.createTextNode(' \u00b7 '));
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'flow-fht-uses-term';
    b.textContent = name;
    b.setAttribute('data-exp', gloss);
    b.addEventListener('click', () => {
      const same = !exp.hidden && exp.textContent === gloss;
      exp.textContent = same ? '' : gloss;
      exp.hidden = same;
    });
    row.appendChild(b);
  });
  row.appendChild(exp);
  return row;
}

/**
 * Build the admin-only Beta nav strip. Shows the current puzzle's
 * position with prev/next buttons that jump to that puzzle's test-mode
 * URL. Disabled at the bounds.
 *
 *   puzzleIndex - [{ id, date, tier }, …] ordered by publish_date asc
 *   puzzle      - the puzzle currently rendered
 *   testMode    - whether the current page is already in test mode
 */
function makeBetaNav(puzzleIndex, puzzle, testMode) {
  const currentId = Number(puzzle && puzzle.puzzle_id);
  let idx = puzzleIndex.findIndex((p) => Number(p.id) === currentId);
  if (idx < 0) idx = 0;
  const cur = puzzleIndex[idx];
  const prev = idx > 0 ? puzzleIndex[idx - 1] : null;
  const next = idx < puzzleIndex.length - 1 ? puzzleIndex[idx + 1] : null;

  const strip = document.createElement('div');
  strip.className = 'flow-fht-beta-nav';
  strip.setAttribute('role', 'navigation');
  strip.setAttribute('aria-label', 'Beta puzzle navigation');

  strip.appendChild(makeNavButton('\u2190', 'Previous puzzle',
    prev ? betaUrlForPuzzle(prev.id) : null));

  const info = document.createElement('div');
  info.className = 'flow-fht-beta-nav-info';
  info.innerHTML =
    '<span class="flow-fht-beta-nav-info-sub">' +
    (testMode ? 'Beta \u00b7 practice mode' : 'Beta nav') +
    '</span>' +
    'Puzzle #' + cur.id + ' \u00b7 ' +
    formatDateShort(cur.date) + ' \u00b7 ' +
    (cur.tier || '').toUpperCase();
  strip.appendChild(info);

  strip.appendChild(makeNavButton('\u2192', 'Next puzzle',
    next ? betaUrlForPuzzle(next.id) : null));

  return strip;
}

function makeNavButton(label, srLabel, url) {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'flow-fht-beta-nav-button';
  btn.textContent = label;
  btn.setAttribute('aria-label', srLabel);
  if (url) {
    btn.addEventListener('click', () => { window.location.href = url; });
  } else {
    btn.disabled = true;
  }
  return btn;
}

function betaUrlForPuzzle(id) {
  // Build the current page URL with ?fht_test=<id>, replacing any
  // existing test marker. Hash is preserved.
  const u = new URL(window.location.href);
  u.searchParams.set('fht_test', String(id));
  return u.toString();
}

function formatDateShort(iso) {
  if (!iso || typeof iso !== 'string') return '';
  // iso looks like "2026-05-13" — render as "5/13/26" to match
  // Jeff's spec example. Plain string parsing avoids timezone surprises.
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return iso;
  return Number(m[2]) + '/' + Number(m[3]) + '/' + m[1].slice(2);
}
